import { Report } from "@/queries/report";
import {
  isHistoricalReportDate,
  sumBreakdownForTerminals,
} from "@/components/disruption/disruptionMetrics";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import React from "react";

interface DisruptionForecastChartProps {
  reports: Report[];
  terminals: string[];
}

export const DisruptionForecastChart: React.FC<DisruptionForecastChartProps> = ({
  reports,
  terminals,
}) => {
  const canceledData = reports.map((report) => {
    const scoped = sumBreakdownForTerminals(
      report.summary_canceled,
      terminals
    );
    const historical = isHistoricalReportDate(report.target_date);
    return {
      y: scoped.total,
      impacted: scoped.impacted,
      report_id: report.id,
      historical,
    };
  });

  const impactedData = canceledData.map((point) => ({
    y: point.impacted,
    report_id: point.report_id,
    historical: point.historical,
  }));

  const options = {
    chart: { type: "column", styledMode: true },
    credits: { enabled: false },
    title: { text: "" },
    plotOptions: {
      column: {
        dataLabels: { enabled: true },
        point: {
          events: {
            click: function (this: { report_id?: number }) {
              if (this.report_id) {
                window.location.href = `/?report_id=${this.report_id}`;
              }
            },
          },
        },
      },
    },
    xAxis: {
      categories: reports.map((report) => report.target_date),
      title: { text: "Date" },
    },
    yAxis: { title: { text: "Count" } },
    series: [
      {
        name: "Canceled Flights",
        data: canceledData.map((point) => ({
          y: point.y,
          report_id: point.report_id,
          className: point.historical
            ? "disruption-historical"
            : "disruption-forward",
        })),
      },
      {
        name: "Passenger Demand Removed",
        data: impactedData.map((point) => ({
          y: point.y,
          report_id: point.report_id,
          className: point.historical
            ? "disruption-historical"
            : "disruption-forward",
        })),
      },
    ],
  };

  return (
    <div className="bg-slate-900 border-slate-600 border drop-shadow flex flex-col mt-4 h-90 md:h-auto">
      <div className="px-4 py-4 text-slate-300 border-b border-slate-700">
        Flight Disruption Forecast
      </div>
      <div className="w-full flex-grow highcharts-dark disruption-forecast-chart p-4">
        <style>
          {`
          .disruption-forecast-chart .disruption-forward {
            fill: rgb(251 191 36 / 0.85);
            stroke: rgb(245 158 11 / 0.9);
          }
          .disruption-forecast-chart .disruption-historical {
            fill: rgb(100 116 139 / 0.85);
            stroke: rgb(71 85 105 / 0.9);
          }
          `}
        </style>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};
